/**
 * Concrete chart colors for the `*Svg` string renderers.
 *
 * The React primitives use CSS custom properties (`var(--he-purple)`, …) so the
 * page theme drives them. A rasterizer (resvg) has no DOM and cannot resolve
 * `var()`, so the standalone SVG strings need literal values. These mirror the
 * light-theme tokens from the frontend design system; keep them in sync with
 * `frontend/src/styles/colors_and_type.css` if the palette changes.
 */
import { CATEGORICAL_LIGHT, CATEGORICAL_OTHER_LIGHT } from './categorical';

export interface ChartColors {
  /** Primary series / brand (--he-purple). */
  purple: string;
  /** Panel background behind the chart. */
  bg: string;
  /** Gridlines / hairlines (--border-1). */
  grid: string;
  /** Primary text (--fg-1). */
  text: string;
  /** Secondary/axis text (--fg-3). */
  subtext: string;
  /** Empty bar track (--bg-3). */
  track: string;
  /** Expansion-zone orange (--he-orange). */
  orange: string;
  /** Secondary accent blue (--he-blue). */
  blue: string;
  /** Positive / "yes" green (matches the online status + KPI up tone). */
  green: string;
  /** Negative / "no" red (matches the KPI down tone). */
  red: string;
}

export const CHART_COLORS: ChartColors = {
  purple: '#5F26FC',
  bg: '#FFFFFF',
  grid: '#ECECEC',
  text: '#1A1A1A',
  subtext: '#7D7D7D',
  track: '#F0F0F0',
  orange: '#F79009',
  blue: '#1088DE',
  green: '#1F8A5B',
  red: '#C2410C',
};

/** Font stack handed to the rasterizer; resvg matches against installed fonts. */
export const CHART_FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, "DejaVu Sans", sans-serif';

/** Known design-system CSS vars → concrete values, for resolving `color` props in rasterized charts. */
const VAR_MAP: Record<string, keyof ChartColors> = {
  '--he-purple': 'purple',
  '--he-orange': 'orange',
  '--he-blue': 'blue',
  '--he-green': 'green',
  '--he-red': 'red',
  '--he-gray-300': 'track',
  // Semantic tokens — the CSS side is oklch(); the rasterizer projects each to
  // its sRGB-hex brand equivalent (resvg can't resolve oklch() reliably).
  '--he-success': 'green',
  '--he-danger': 'red',
  '--he-warning': 'orange',
  '--he-info': 'blue',
  '--he-neutral': 'subtext',
};

/**
 * Light-surface hex for a categorical CSS token (`--he-cat-1`..`--he-cat-6`, or
 * `--he-cat-other`), else `undefined` when `name` isn't a categorical token. The
 * rasterizer is light-only, so these always resolve to the light palette.
 */
function categoricalVarHex(name: string): string | undefined {
  if (name === '--he-cat-other') return CATEGORICAL_OTHER_LIGHT;
  const m = name.match(/^--he-cat-([1-9]\d*)$/);
  if (!m) return undefined;
  const slot = Number(m[1]) - 1;
  return slot < CATEGORICAL_LIGHT.length ? CATEGORICAL_LIGHT[slot] : CATEGORICAL_OTHER_LIGHT;
}

/**
 * Resolve a `BarDatum.color` (or any chart color prop) to a concrete value the
 * rasterizer can use. Passes through hex/rgb/hsl; maps the categorical tokens
 * (`--he-cat-*`) and known semantic `var(--he-*)` tokens; falls back to the
 * primary series color for anything unrecognized.
 */
export function resolveCssColor(color: string | undefined, colors: ChartColors = CHART_COLORS): string {
  if (!color) return colors.purple;
  const varMatch = color.match(/^var\(\s*(--[\w-]+)\s*\)$/);
  if (varMatch) {
    const name = varMatch[1];
    const cat = categoricalVarHex(name);
    if (cat) return cat;
    const key = VAR_MAP[name];
    return key ? colors[key] : colors.purple;
  }
  // Strict allow-list, not a prefix match: a hex triplet/quad, or rgb()/hsl()
  // (optional alpha) containing only numeric/separator chars. This rejects
  // attribute-breakout payloads like `rgb(0,0,0)" onload="...` that a prefix
  // check would pass straight into a `fill="..."` attribute.
  if (/^#[0-9a-f]{3,8}$/i.test(color)) return color;
  if (/^(rgb|hsl)a?\(\s*[\d\s.,%/]+\)$/i.test(color)) return color;
  return colors.purple;
}
